import api from './api';

/**
 * Service for fetching player props and value plays from the OLAP table
 */
const propsService = {
  /**
   * Get player props for a sport on a specific date
   * @param {string} sport - Sport (nba, nhl, nfl)
   * @param {Object} options - Query options
   * @param {string} options.gameDate - Date in YYYY-MM-DD format (defaults to today)
   * @param {string} options.playerName - Filter by player name
   * @param {string} options.statType - Filter by stat type
   * @param {boolean} options.valuePlayOnly - Only return value plays
   * @param {number} options.minEdge - Minimum edge threshold
   * @param {number} options.limit - Max results
   */
  async getProps(sport, options = {}) {
    const params = new URLSearchParams();
    if (options.gameDate) params.append('game_date', options.gameDate);
    if (options.playerName) params.append('player_name', options.playerName);
    if (options.statType) params.append('stat_type', options.statType);
    if (options.valuePlayOnly) params.append('value_plays_only', 'true');
    if (options.minEdge) params.append('min_edge', options.minEdge);
    if (options.limit) params.append('limit', options.limit);

    const response = await api.get(`/game-analytics/props/${sport}?${params.toString()}`);
    return response.data;
  },

  /**
   * Get high-value plays where ML significantly disagrees with sportsbook
   * @param {string} sport - Sport (nba, nhl, nfl)
   * @param {Object} options - Query options
   * @param {string} options.gameDate - Date in YYYY-MM-DD format
   * @param {string} options.direction - 'over', 'under', or null for both
   * @param {number} options.minEdge - Minimum edge (default 2.0)
   * @param {number} options.limit - Max results
   */
  async getValuePlays(sport, options = {}) {
    const params = new URLSearchParams();
    if (options.gameDate) params.append('game_date', options.gameDate);
    if (options.direction) params.append('direction', options.direction);
    if (options.minEdge) params.append('min_edge', options.minEdge);
    if (options.limit) params.append('limit', options.limit);

    const response = await api.get(`/game-analytics/props/${sport}/value-plays?${params.toString()}`);
    return response.data;
  },

  /**
   * Get props for a specific matchup (by filtering player names)
   * @param {string} sport - Sport
   * @param {string} gameDate - Date
   * @param {string} matchup - Matchup string like "PHX @ OKC"
   */
  async getPropsForMatchup(sport, gameDate, matchup) {
    // Get all props for the date and filter by matchup on client side
    // This is efficient since we're fetching from a pre-indexed OLAP table
    const response = await api.get(`/game-analytics/props/${sport}`, {
      params: {
        game_date: gameDate,
        limit: 500 // Get more to ensure we capture all matchup props
      }
    });

    const data = response.data;
    if (matchup && data.props) {
      data.props = data.props.filter(p =>
        p.matchup && p.matchup.toLowerCase().includes(matchup.toLowerCase())
      );
    }
    return data;
  }
};

export default propsService;
